import type {
  ApiError,
  ChatMessage,
  GameState,
  JoinGameResponse,
  ProfileDashboard,
  ProfileSessionResponse,
  ProfileSummary,
} from "../../shared/game.ts";

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly code = "request_failed",
    public readonly status = 0,
  ) {
    super(message);
  }
}

async function fetchResponse(path: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(path, init);
  } catch {
    throw new ApiRequestError("Не удалось связаться с сервером. Проверьте интернет.", "network_error");
  }
}

async function apiError(response: Response): Promise<ApiRequestError> {
  const error = (await response.json().catch(() => null)) as ApiError | null;
  return new ApiRequestError(error?.message || "Сервер не смог выполнить запрос.", error?.error, response.status);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetchResponse(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  const payload = (await response.json().catch(() => null)) as T | ApiError | null;
  if (!response.ok) {
    const error = payload as ApiError | null;
    throw new ApiRequestError(error?.message || "Сервер не смог выполнить запрос.", error?.error, response.status);
  }
  return payload as T;
}

export function createGame(input: {
  friendId: string;
  preferredSide: "white" | "black" | "random";
  initialSeconds: number;
  incrementSeconds: number;
}, profileToken: string): Promise<JoinGameResponse> {
  return request<JoinGameResponse>("/api/games", {
    method: "POST",
    headers: { "X-Profile-Token": profileToken },
    body: JSON.stringify(input),
  });
}

export function createProfile(name: string): Promise<ProfileSessionResponse> {
  return request<ProfileSessionResponse>("/api/profiles", { method: "POST", body: JSON.stringify({ name }) });
}

export function getProfileDashboard(profileToken: string): Promise<ProfileDashboard> {
  return request<ProfileDashboard>("/api/profile", { headers: { "X-Profile-Token": profileToken } });
}

export function updateProfile(profileToken: string, name: string): Promise<ProfileDashboard> {
  return request<ProfileDashboard>("/api/profile", {
    method: "PATCH",
    headers: { "X-Profile-Token": profileToken },
    body: JSON.stringify({ name }),
  });
}

export function getProfileByFriendCode(code: string): Promise<ProfileSummary> {
  return request<ProfileSummary>(`/api/friends/code/${encodeURIComponent(code)}`);
}

export async function sendFriendRequest(profileToken: string, code: string): Promise<ProfileDashboard> {
  const result = await request<{ dashboard: ProfileDashboard }>("/api/friend-requests", {
    method: "POST",
    headers: { "X-Profile-Token": profileToken },
    body: JSON.stringify({ code }),
  });
  return result.dashboard;
}

export async function respondToFriendRequest(
  profileToken: string,
  requestId: string,
  accept: boolean,
): Promise<ProfileDashboard> {
  const result = await request<{ dashboard: ProfileDashboard }>(
    `/api/friend-requests/${encodeURIComponent(requestId)}/${accept ? "accept" : "decline"}`,
    { method: "POST", headers: { "X-Profile-Token": profileToken } },
  );
  return result.dashboard;
}

export function acceptGameInvitation(profileToken: string, invitationId: string): Promise<JoinGameResponse> {
  return request<JoinGameResponse>(`/api/game-invitations/${encodeURIComponent(invitationId)}/accept`, {
    method: "POST",
    headers: { "X-Profile-Token": profileToken },
  });
}

export async function declineGameInvitation(profileToken: string, invitationId: string): Promise<ProfileDashboard> {
  const result = await request<{ dashboard: ProfileDashboard }>(
    `/api/game-invitations/${encodeURIComponent(invitationId)}/decline`,
    { method: "POST", headers: { "X-Profile-Token": profileToken } },
  );
  return result.dashboard;
}

export function joinGame(gameId: string, name: string, token: string | null): Promise<JoinGameResponse> {
  return request<JoinGameResponse>(`/api/games/${encodeURIComponent(gameId)}/join`, {
    method: "POST",
    body: JSON.stringify({ name, token }),
  });
}

export function getGameState(gameId: string): Promise<GameState> {
  return request<GameState>(`/api/games/${encodeURIComponent(gameId)}`);
}

export async function uploadVoiceMessage(
  gameId: string,
  token: string,
  audio: Blob,
  durationMs: number,
): Promise<ChatMessage> {
  const response = await fetchResponse(`/api/games/${encodeURIComponent(gameId)}/voice-messages`, {
    method: "POST",
    headers: {
      "Content-Type": audio.type || "application/octet-stream",
      "X-Game-Token": token,
      "X-Voice-Duration-Ms": String(Math.round(durationMs)),
    },
    body: audio,
  });
  if (!response.ok) throw await apiError(response);
  return await response.json() as ChatMessage;
}

export async function getVoiceMessageAudio(gameId: string, messageId: number, token: string): Promise<Blob> {
  const response = await fetchResponse(
    `/api/games/${encodeURIComponent(gameId)}/voice-messages/${messageId}/audio`,
    { headers: { "X-Game-Token": token } },
  );
  if (!response.ok) throw await apiError(response);
  return await response.blob();
}

export async function getPushPublicKey(): Promise<string> {
  const result = await request<{ publicKey: string }>("/api/push/public-key");
  return result.publicKey;
}

export function savePushSubscription(
  gameId: string,
  token: string,
  subscription: PushSubscriptionJSON,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/games/${encodeURIComponent(gameId)}/push`, {
    method: "POST",
    body: JSON.stringify({ token, subscription }),
  });
}

export function removePushSubscription(gameId: string, token: string, endpoint: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/games/${encodeURIComponent(gameId)}/push`, {
    method: "DELETE",
    body: JSON.stringify({ token, endpoint }),
  });
}

export function saveProfilePushSubscription(
  profileToken: string,
  subscription: PushSubscriptionJSON,
): Promise<{ ok: true }> {
  return request<{ ok: true }>("/api/profile/push", {
    method: "POST",
    headers: { "X-Profile-Token": profileToken },
    body: JSON.stringify({ subscription }),
  });
}

export function removeProfilePushSubscription(profileToken: string, endpoint: string): Promise<{ ok: true }> {
  return request<{ ok: true }>("/api/profile/push", {
    method: "DELETE",
    headers: { "X-Profile-Token": profileToken },
    body: JSON.stringify({ endpoint }),
  });
}
